import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ComunicadoCarrossel , fmtBRL} from "@atlas/ui/web";
import type { Comunicado } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import type { MatriculaInfo } from "../../lib/matricula-data";
import { readActiveMatricula, hydrateMatriculas, STORAGE_KEY_META, STORAGE_KEY_ID } from "../../lib/matricula-data";

const PRODUTO_LABEL: Record<string, string> = {
  EMPRESTIMO: "Empréstimo Consignado",
  CARTAO_CONSIGNADO: "Cartão de Crédito Consignado",
  CARTAO_BENEFICIOS: "Cartão Benefício Consignado",
};



// Cliente pediu 24/07/2026: sem fallback hardcoded. Se a averbadora nao
// publicou nada pra publico=servidor, o carrossel some (ComunicadoCarrossel
// ja retorna null pra lista vazia).

/** Converte o shape do SDK ({linkLabel, linkHref}) pra o shape que o
 *  ComunicadoCarrossel do @atlas/ui espera ({link: {label, href}}). */
function toCarrosselShape(c: Comunicado) {
  return {
    id: c.id,
    titulo: c.titulo,
    corpo: c.corpo,
    ...(c.linkHref ? { link: { label: c.linkLabel ?? "Saiba mais", href: c.linkHref } } : {}),
  };
}

export function ServidorDashboard() {
  const nav = useNavigate();
  const [info, setInfo] = useState<MatriculaInfo | null>(() => readActiveMatricula());

  useLayoutEffect(() => {
    const active = readActiveMatricula();
    if (!active) {
      nav("/servidor/selecionar-matricula", { replace: true });
    } else {
      setInfo(active);
    }
  }, [nav]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY_META || e.key === STORAGE_KEY_ID) {
        setInfo(readActiveMatricula());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Re-hidrata margem/contratos do backend ao entrar no dashboard e a cada 8s.
  // Sem isso, o card de margem aqui mostra o snapshot antigo do localStorage —
  // servidor que acabou de fazer um "Simular" voltava pro dashboard e nao via
  // a margem descontada ate deslogar/entrar de novo.
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      await hydrateMatriculas();
      if (!stop) setInfo(readActiveMatricula());
    };
    void tick();
    const id = window.setInterval(tick, 8000);
    return () => { stop = true; window.clearInterval(id); };
  }, []);

  const primeiroNome = useMemo(() => (info?.nome ?? "").split(" ")[0] ?? "", [info?.nome]);

  // Comunicados publicados pela averbadora para publico=servidor.
  // Sem fallback hardcoded (removido 24/07/2026). Carrossel some quando a
  // averbadora nao publicou nada — ComunicadoCarrossel ja retorna null pra
  // lista vazia.
  const comunicadosQ = useQuery({
    queryKey: ["servidor", "comunicados"],
    queryFn: () => atlas.servidor.comunicados(),
    staleTime: 5 * 60_000,
  });
  const comunicadosCarrossel = useMemo(
    () => (comunicadosQ.data?.comunicados ?? []).map(toCarrosselShape),
    [comunicadosQ.data],
  );

  // Banners patrocinados (vitrine) — cadastrados pela averbadora. Impressao
  // e clique geram receita CPC pra averbadora (modelo cliente 28/07/2026).
  const vitrineQ = useQuery({
    // A matricula entra na CHAVE, senao trocar de vinculo servia o cache do
    // anterior — e a vitrine agora depende da prefeitura (bancos que atendem).
    queryKey: ["servidor", "vitrine", info?.matricula],
    queryFn: () => atlas.servidor.vitrine(info?.matricula),
    staleTime: 5 * 60_000,
  });
  const banners = vitrineQ.data?.banners ?? [];
  // Registra impressao uma vez por sessao por banner exibido.
  useEffect(() => {
    if (banners.length === 0) return;
    const jaEnviados = new Set<string>();
    for (const b of banners) {
      if (jaEnviados.has(b.id)) continue;
      jaEnviados.add(b.id);
      void atlas.servidor.vitrineImpressao(b.id).catch(() => undefined);
    }
  }, [banners]);
  const onClicarBanner = (bannerId: string) => {
    void atlas.servidor.vitrineClique(bannerId).catch(() => undefined);
  };

  if (!info) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 1000, margin: "0 auto", width: "100%" }}>
      {/* Header */}
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Meu painel
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "2rem", letterSpacing: "-0.02em" }}>Olá, {primeiroNome}</h1>
        <div style={{ color: "var(--text-muted)", marginTop: 4 }}>
          Matrícula <b style={{ color: "var(--text)" }}>{info.matricula}</b> · {info.prefeitura}
        </div>
      </header>

      {/* 3 cards de margem lado a lado — botao de acao em cada
          (Emprestimo, Cartao Consignado, Cartao Beneficio). */}
      <MinhaMargemPorModalidade info={info} />

      {/* Carrossel de comunicados/vitrine da averbadora — publico=servidor.
          Cliente pediu explicitamente o carrossel NO MEIO, entre os cards de
          margem e os 2 blocos de atalho abaixo. Autoplay 3s (default do
          componente e 6s) — cliente pediu mais rapido. */}
      <ComunicadoCarrossel comunicados={comunicadosCarrossel} autoplayMs={5000} />

      {/* Banners da vitrine — CPC. Cada clique gera receita pra averbadora. */}
      {banners.length > 0 ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
          {banners.map((b) => (
            <a
              key={b.id}
              href="#"
              onClick={(e) => { e.preventDefault(); onClicarBanner(b.id); }}
              style={{
                display: "block",
                background: "var(--bg-elev)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                padding: 14,
                textDecoration: "none",
                color: "var(--text)",
                cursor: "pointer",
              }}
            >
              <div style={{ fontSize: 11, letterSpacing: ".08em", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", marginBottom: 4 }}>
                {b.bancoNome}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{b.titulo}</div>
              {b.imagemUrl ? (
                <img src={b.imagemUrl} alt={b.titulo} style={{ marginTop: 10, width: "100%", borderRadius: 8, maxHeight: 140, objectFit: "cover" }} />
              ) : null}
            </a>
          ))}
        </div>
      ) : null}

      {/* 2 blocos grandes lado a lado — Telemedicina (esquerda) e Portabilidade (direita).
          Cliente pediu essa ordem: "Telemedicina e a Portabilidade".
          Portabilidade vai DIRETO pra /servidor/portabilidade (rota curta configurada
          em outra sessao pra pular o hub do MarketPlace). */}
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(2, 1fr)" }}>
        <AtalhoCard
          titulo="Telemedicina"
          descricao="Consulta médica online descontada em folha, para você e sua família."
          icon="🏥"
          accent="emerald"
          onClick={() => nav("/servidor/beneficios")}
        />
        <AtalhoCard
          titulo="Portabilidade"
          descricao="Consolide em outra instituição com taxa menor e reduza sua parcela mensal."
          icon="🔁"
          accent="gold"
          onClick={() => nav("/servidor/portabilidade")}
        />
      </div>
    </div>
  );
}

/** Card grande dark com 3 quadrados de margem lado a lado. Herdado do trabalho
 *  do colega: cliente aprovou o formato — mantido como esta. */
function MinhaMargemPorModalidade({ info }: { info: MatriculaInfo }) {
  // Defensivo: cache antigo de localStorage pode nao ter `margens_por_tipo` (schema
  // pre-3-buckets). Fallback pra array vazio evita crash — os cards ainda renderizam
  // zerados ate a re-hidratacao trazer o shape correto.
  const porTipo = new Map((info.margem?.margens_por_tipo ?? []).map((m) => [m.tipo, m]));
  const ordem = ["EMPRESTIMO", "CARTAO_CONSIGNADO", "CARTAO_BENEFICIOS"] as const;
  const linhas = ordem.map((t) => porTipo.get(t) ?? { tipo: t, total: 0, disponivel: 0 });

  return (
    <article style={{
      background: "var(--surface-solid)",
      border: "1px solid var(--border-strong)",
      borderRadius: 16,
      padding: 20,
      color: "var(--text)",
      boxShadow: "var(--shadow-md)",
    }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
        {linhas.map((m) => (
          <MargemCard key={m.tipo} data={m} />
        ))}
      </div>
    </article>
  );
}

const ACAO_POR_TIPO: Record<string, { label: string; href: string } | undefined> = {
  EMPRESTIMO: { label: "Simular →", href: "/servidor/simular?produto=emprestimo" },
  CARTAO_CONSIGNADO: { label: "Simular →", href: "/servidor/simular?produto=cartao_consignado" },
  // Cartao Beneficio segue a mesma dinamica do Consignado: simula na tela
  // dedicada (/servidor/simular?produto=cartao_beneficio) e cria a proposta
  // com tipoMargem=CARTAO_BENEFICIOS. No banco, a proposta cai no submenu
  // "Cartao Beneficio" (nao em "Cartao"), gracas ao filtro por tipoMargem.
  CARTAO_BENEFICIOS: { label: "Simular →", href: "/servidor/simular?produto=cartao_beneficio" },
};

function MargemCard({ data }: { data: { tipo: string; total: number; disponivel: number } }) {
  const nav = useNavigate();
  const utilizado = Math.max(0, data.total - data.disponivel);
  const pctUtilizado = data.total > 0 ? (utilizado / data.total) * 100 : 0;
  const pctLivre = 100 - pctUtilizado;
  const barra = utilizado === 0 ? "var(--emerald-500)" : pctUtilizado > 80 ? "var(--danger-500)" : "var(--gold-500)";
  const label = PRODUTO_LABEL[data.tipo] ?? data.tipo;
  const acao = ACAO_POR_TIPO[data.tipo];

  return (
    <div style={{
      background: "var(--bg-elev-2)",
      border: "1px solid var(--border)",
      borderRadius: 14,
      padding: 18,
      display: "flex",
      flexDirection: "column",
      gap: 14,
      minWidth: 0,
    }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", lineHeight: 1.3 }}>{label}</div>

      <div>
        <div style={{ height: 5, background: "var(--border)", borderRadius: 4, overflow: "hidden" }}>
          <div style={{ width: `${pctUtilizado}%`, height: "100%", background: barra, transition: "width .4s ease" }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5, fontSize: 11 }}>
          <span style={{ color: utilizado > 0 ? "var(--gold-500)" : "var(--text-dim)" }}>
            {utilizado === 0 ? "0%" : `${pctUtilizado.toFixed(1)}% util.`}
          </span>
          <span style={{ color: "var(--emerald-500)" }}>{pctLivre.toFixed(1)}% livre</span>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <ValorLinha label="Total" value={fmtBRL(data.total)} color="var(--text)" />
        <ValorLinha label="Utilizado" value={fmtBRL(utilizado)} color={utilizado > 0 ? "var(--gold-500)" : "var(--text-dim)"} />
        <ValorLinha label="Disponível" value={fmtBRL(data.disponivel)} color="var(--emerald-500)" bold />
      </div>

      {acao ? (
        <button
          type="button"
          onClick={() => nav(acao.href)}
          style={{
            marginTop: 4,
            padding: "10px 12px",
            borderRadius: 10,
            border: "none",
            background: "var(--emerald-500)",
            color: "white",
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
            width: "100%",
          }}
        >
          {acao.label}
        </button>
      ) : null}
    </div>
  );
}

function ValorLinha({ label, value, color, bold }: { label: string; value: string; color: string; bold?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 6 }}>
      <span style={{ fontSize: 11, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-dim)", fontWeight: 700 }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: bold ? 800 : 700, color, textAlign: "right", whiteSpace: "nowrap" }}>{value}</span>
    </div>
  );
}

function AtalhoCard({
  titulo, descricao, icon, accent, onClick,
}: {
  titulo: string; descricao: string; icon: string;
  accent: "emerald" | "gold" | "navy"; onClick: () => void;
}) {
  const accentColor =
    accent === "emerald" ? "var(--emerald-500)" : accent === "gold" ? "var(--gold-500)" : "var(--accent)";
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: "left", cursor: "pointer",
        background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14,
        padding: 18, display: "flex", flexDirection: "column", gap: 8,
        // height:100% + parent grid align-items:stretch (default) faz o botao
        // esticar pra altura do card mais alto; assim marginTop:auto no "Ver mais"
        // realmente empurra ele pro rodape e alinha as 2 pills horizontalmente.
        height: "100%",
        transition: "transform .12s ease, border-color .12s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = accentColor;
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--border)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <span style={{ fontSize: 28 }}>{icon}</span>
      <span style={{ fontWeight: 700, fontSize: "1.05rem", color: "var(--text)" }}>{titulo}</span>
      <span style={{ fontSize: ".88rem", color: "var(--text-muted)", lineHeight: 1.4 }}>{descricao}</span>
      {/* "Ver mais" com aparencia de botao (mesmo estando dentro de um <button> pai —
          html-wise vira <span> visualmente estilizado, e o clique cai no pai).
          marginTop: auto empurra o Ver mais pro fim do card, garantindo que os 2
          botoes fiquem alinhados na mesma baseline mesmo com descricoes de tamanhos diferentes. */}
      <span
        style={{
          alignSelf: "flex-start",
          marginTop: "auto",
          padding: "6px 14px",
          borderRadius: 999,
          background: `color-mix(in srgb, ${accentColor} 15%, transparent)`,
          border: `1px solid ${accentColor}`,
          color: accentColor,
          fontWeight: 700,
          fontSize: ".85rem",
        }}
      >
        Ver mais →
      </span>
    </button>
  );
}

